import {
  DeviceResponse,
  EmptyResponse,
  PresetProviderGroup,
  SocialAccount,
  TypedApiRequestParams,
  UserDetail,
  UserDetailUpdate,
  UserDeviceUpdate,
  UserProfile,
  UserProfileUpdate,
  UserSearchResult,
} from '../types';

const getUserDetail = (): TypedApiRequestParams<UserDetail> => ({
  url: 'user/me',
  method: 'GET',
  authRequired: true,
});

const updateUserDetail = (
  userDetailDict: UserDetailUpdate,
): TypedApiRequestParams<UserDetail> => ({
  url: 'user/me',
  method: 'PATCH',
  params: userDetailDict,
  authRequired: true,
});

const deleteUseraccount = (): TypedApiRequestParams<EmptyResponse> => ({
  url: 'user/account/delete',
  method: 'POST',
  authRequired: true,
});

const updateUserProfile = (
  userProfileDict: UserProfileUpdate,
): TypedApiRequestParams<UserProfile> => ({
  url: 'user/profile',
  method: 'PATCH',
  params: userProfileDict,
  authRequired: true,
});

/** The same fields, minus those whose value is `undefined`. */
const withoutUnknown = (
  fields: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined),
  );

const updateUserDevice = ({
  deviceId,
  os,
  osVersion,
  name,
  appVersion,
  fbToken,
  deviceLocation,
  language,
}: UserDeviceUpdate): TypedApiRequestParams<DeviceResponse> => ({
  url: 'user/devices/',
  method: 'POST',
  // An unknown (`undefined`) value is left out rather than sent as null: the
  // backend keeps a missing key and clears a null one (!760), so a
  // registration sent before the FCM token is known no longer wipes it (#431).
  params: withoutUnknown({
    device_id: deviceId,
    os,
    os_version: osVersion,
    name,
    app_version: appVersion,
    fb_token: fbToken,
    last_position: deviceLocation,
    language,
  }),
  authRequired: false,
});

const getSocialAccounts = (): TypedApiRequestParams<SocialAccount[]> => ({
  url: 'user/social-accounts',
  method: 'GET',
  authRequired: true,
});

const disconnectSocialAccount = (
  accountId: number,
): TypedApiRequestParams<EmptyResponse> => ({
  url: `user/social-accounts/${accountId}/disconnect/`,
  method: 'POST',
  authRequired: true,
});

const searchUsers = (
  query: string,
): TypedApiRequestParams<UserSearchResult[]> => ({
  url: 'user/search',
  method: 'GET',
  params: {q: query},
  authRequired: true,
});

/**
 * Ready-made "Login As" targets, grouped by station provider.
 *
 * Superuser-only. The list is computed from live data on every request, so no
 * email address is ever baked into the app bundle.
 */
const getPresetUsers = (): TypedApiRequestParams<PresetProviderGroup[]> => ({
  url: 'user/presets',
  method: 'GET',
  authRequired: true,
});

export default {
  getUserDetail,
  updateUserDetail,
  deleteUseraccount,
  updateUserProfile,
  updateUserDevice,
  getSocialAccounts,
  disconnectSocialAccount,
  searchUsers,
  getPresetUsers,
};
